import {
  takeLatest,
  takeLeading,
  put,
  select,
  call,
  delay,
  all,
  take,
  takeEvery,
  fork,
  actionChannel,
  spawn,
} from 'redux-saga/effects';
import axios from 'axios';
import dayjs from 'dayjs';
import Resizer from 'react-image-file-resizer';
import allSettled from 'promise.allsettled';
import isEqual from 'lodash.isequal';
import {
  Timestamp,
  arrayUnion,
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  getDoc,
  setDoc,
  writeBatch,
  deleteDoc,
  where,
  query,
} from 'firebase/firestore';
import { ref, uploadString, deleteObject, uploadBytes } from 'firebase/storage';
import { logEvent } from 'firebase/analytics';

import { db, storage, analytics } from 'firebaseConfig';

import {
  createDocumentChangeChannel,
  getURLFromCSSURL,
  convertDownloadURLToGS,
  convertGSToDownloadURL,
  nullFunction,
  isURLMissingTrailingSlash,
  getFileNameWithoutExt,
} from 'utils';
import {
  defaultEmailPreferences,
  pageLoadingErrorMessage,
} from 'utils/contentProvider';
import findPageLinks from 'utils/findPageLinks';

import {
  showSnackbar,
  hideSnackbar,
  toggleSimilarPagesModal,
} from 'modules/App/store/actions';
import { projectRequest } from 'modules/Projects/store/actions';
import { pushNotification, signInSuccess } from 'modules/Users/store/actions';

import {
  actions,
  allPagesSuccess,
  currentVersionSuccess,
  showLoading,
  hideLoading,
  toggleVersionLoading,
  pageSuccess,
  cancelPageVersionListener,
  pushPagesCreating,
  popPagesCreating,
  addWebPage,
} from './actions';
import * as selectors from './selectors';

function* delayAndHideSnackbar() {
  yield delay(5000);
  yield put(hideSnackbar());
}

/**
 * Creates a page title from the URL provided.
 * It'll return hostname in case of the index page, else
 * the last directory of the path.
 * @param {string} url
 * @returns {string}
 */

const createPageTitle = (url) => {
  const urlObj = new URL(url);

  if (urlObj.pathname === '/') return urlObj.host;

  const splits = urlObj.pathname.split('/').filter((i) => Boolean(i));

  if (splits.length > 0) return splits[splits.length - 1];

  return urlObj.host;
};

function* addPageSaga({ payload, callback = nullFunction, navigate }) {
  const project = yield select(selectors.currentProject);
  const pages = yield select(selectors.pages);
  const pagesColRef = collection(db, 'projects', project.id, 'pages');
  const title = createPageTitle(payload.url);
  const urlMatchedPages = pages
    .filter((p) => {
      const checkURL = p.screenshotSource || p.url;

      return !checkURL.endsWith('/') && payload.url.endsWith('/')
        ? `${checkURL}/` === payload.url
        : checkURL === payload.url;
    })
    .filter((p) =>
      payload.isScreenshot ? Boolean(p.screenshotSource) : !p.screenshotSource,
    );

  const approvedMatchedPages = urlMatchedPages.filter(
    (p) => p.isApproved === true,
  );
  const unarchivedUrlExists = urlMatchedPages.filter(
    (p) => p.isArchived === false,
  );
  const timestamp = Timestamp.now();
  const user = yield select(selectors.user);
  let newPageDocRef;
  let newVersionDocRef;
  const newPage = {
    id: 'new',
    createdAt: timestamp,
    modifiedAt: timestamp,
    url: payload.isScreenshot ? 'image' : payload.url,
    title:
      urlMatchedPages.length > 0
        ? `(${urlMatchedPages.length + 1}) ${title}`
        : title,
    versions: [],
    isArchived: false,
    ...(payload.isScreenshot ? { screenshotSource: payload.url } : {}),
  };
  const newVersion = {
    createdAt: timestamp,
    createdBy: user.uid,
  };

  if (payload.copyUnresolvedComments) {
    // Copy unresovled comments.

    const currentVersion = yield select(selectors.currentPageVersion);

    newVersion.comments = currentVersion.comments
      .filter((c) => !c.isResolved)
      .map((c) => ({ ...c, copiedFromVersion: currentVersion.id }));
  }

  if (unarchivedUrlExists.length - approvedMatchedPages.length > 0) {
    yield put(pushPagesCreating(unarchivedUrlExists[0].id));

    // Add new version to existing page.
    newPageDocRef = doc(pagesColRef, unarchivedUrlExists[0].id);
    newVersionDocRef = yield addDoc(
      collection(newPageDocRef, 'versions'),
      newVersion,
    );

    const versionsQuerySnap = yield getDocs(
      collection(newPageDocRef, 'versions'),
    );
    const versionsQueryDocSnap = versionsQuerySnap.docs.map((docSnap) => ({
      id: docSnap.id,
      createdAt: docSnap.get('createdAt'),
      createdBy: docSnap.get('createdBy') || user.uid,
      numberOfComments: docSnap.get('comments')?.length || 0,
      numberOfSuggestions: docSnap.get('inspectChanges')?.length || 0,
      title: docSnap.get('title') || null,
    }));

    yield updateDoc(newPageDocRef, {
      modifiedAt: timestamp,
      versions: versionsQueryDocSnap,
    });
    newPage.versions = versionsQueryDocSnap;
  } else {
    // Add new page and version.
    const { id, ...newPageAdd } = newPage;

    newPageDocRef = yield addDoc(pagesColRef, newPageAdd);
    yield put(pushPagesCreating(newPageDocRef.id));
    newVersionDocRef = yield addDoc(
      collection(newPageDocRef, 'versions'),
      newVersion,
    );

    const newVersionDocSnap = yield getDoc(newVersionDocRef);
    const newVersionsArray = [
      {
        id: newVersionDocRef.id,
        createdAt: newVersionDocSnap.get('createdAt'),
        createdBy: user.uid,
        numberOfComments: 0,
        numberOfSuggestions: 0,
      },
    ];

    yield updateDoc(newPageDocRef, {
      modifiedAt: timestamp,
      versions: newVersionsArray,
    });
    newPage.versions = newVersionsArray;
  }

  newPage.id = newPageDocRef.id;

  if (payload.isScreenshot) {
    try {
      yield call(
        axios.post,
        `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/generatePageScreenshot`,
        {
          url: payload.url,
          context: {
            projectID: project.id,
            pageID: newPageDocRef.id,
            versionID: newVersionDocRef.id,
          },
        },
      );
    } catch (err) {
      yield deleteDoc(newVersionDocRef);
      if (newPage.versions.length === 1) yield deleteDoc(newPageDocRef);
      yield put(popPagesCreating(newPageDocRef.id));
      yield put(showSnackbar(err.response.data));
      yield spawn(delayAndHideSnackbar);
      return;
    }
  } else
    yield uploadString(
      ref(
        storage,
        `pages/${project.id}/${newPageDocRef.id}/${newVersionDocRef.id}/initial-HTML.html`,
      ),
      payload.data,
    );

  yield updateDoc(doc(db, 'projects', project.id), {
    modifiedAt: timestamp,
    /* eslint-disable indent */
    pages: [
      ...pages
        .map((p) => ({
          id: p.id,
          url: p.url,
          createdAt: p.createdAt,
          numberOfVersions: p.versions.length,
        }))
        .map((p) =>
          p.id === newPage.id &&
          unarchivedUrlExists.length - approvedMatchedPages.length > 0
            ? {
                ...p,
                numberOfVersions: p.numberOfVersions + 1,
              }
            : p,
        ),
    ].concat(
      unarchivedUrlExists.length === 0
        ? [
            {
              id: newPage.id,
              createdAt: newPage.createdAt,
              url: newPage.url,
              numberOfVersions: 1,
            },
          ]
        : [],
    ),
    /* eslint-enable indent */
  });

  yield put(popPagesCreating(newPageDocRef.id));
  yield put(
    showSnackbar(
      `${
        payload.paginate
          ? `[${payload.paginate.index}/${payload.paginate.total}]`
          : ''
      } New page${payload.isScreenshot ? ' screenshot' : ''} ${
        unarchivedUrlExists.length - approvedMatchedPages.length > 0
          ? 'version '
          : ''
      }added to your project.`,
    ),
  );

  if (pages.length === 0 || payload.isConsole === true) {
    navigate(
      `/project/${project.id}/${newPageDocRef.id}/${newVersionDocRef.id}/comment`,
    );
    yield put(pageSuccess(newPage));
  }

  logEvent(analytics, 'page_add', {
    event_user: (user && user.uid) || null,
    project: (project && project.id) || null,
    project_page: (newPageDocRef && newPageDocRef.id) || null,
    project_page_version: (newVersionDocRef && newVersionDocRef.id) || null,
  });

  callback({ type: 'success' });

  // Push notifications.

  yield put(
    pushNotification({
      type: ['page'],
      context: {
        projectID: project.id,
        pageID: newPageDocRef.id,
        versionID: newVersionDocRef.id,
        url: payload.url,
      },
    }),
  );

  if (
    payload.paginate ? payload.paginate.index === payload.paginate.total : true
  )
    yield spawn(delayAndHideSnackbar);
}

export function* watchAddPage() {
  const addPageChannel = yield actionChannel(actions.ADD_WEB_PAGE);

  while (true) {
    const payload = yield take(addPageChannel);

    yield call(addPageSaga, payload);
  }
}

function* pagesChannel({ projectID, navigate }, pagesColSnap) {
  const project = yield select(selectors.currentProject);
  let pagesArray = [];
  let pagesEditCounts = [];
  const updatePagesWithCounts = [];

  if ((!project || !project.id) && projectID)
    yield put(projectRequest(projectID, navigate));
  yield put(showLoading());
  pagesArray = pagesColSnap.docs
    .map((docSnap) => {
      let snap = {};

      if (
        !docSnap.get('thumbnail')?.startsWith('data') &&
        !docSnap.get('thumbnail')?.startsWith('http')
      )
        snap.thumbnail = convertGSToDownloadURL(
          `thumbnails/${project.id || projectID}-${docSnap.id}.jpg`,
          docSnap.get('modifiedAt')
            ? String(docSnap.get('modifiedAt').seconds)
            : false,
        );

      if (
        docSnap.get('url') &&
        docSnap.get('url') !== 'image' &&
        isURLMissingTrailingSlash(docSnap.get('url'))
      )
        snap.url = `${docSnap.get('url')}/`;

      snap = {
        id: docSnap.id,
        ...docSnap.data(),
        ...snap,
      };

      return snap;
    })
    .sort((a, b) => b.modifiedAt.valueOf() - a.modifiedAt.valueOf());

  pagesEditCounts = pagesArray.reduce((a, p) => {
    if (
      p.versions &&
      p.versions.length > 0 &&
      p.versions.some(
        (v) =>
          typeof v.numberOfSuggestions !== 'number' ||
          typeof v.numberOfComments !== 'number',
      )
    )
      return [
        ...a,
        ...p.versions.map((v) =>
          getDoc(
            doc(db, 'projects', projectID, 'pages', p.id, 'versions', v.id),
          ),
        ),
      ];
    return a;
  }, []);

  if (pagesEditCounts.length > 0) {
    pagesEditCounts = yield all(pagesEditCounts);
    pagesEditCounts = pagesEditCounts.map((i) => ({
      id: i.id,
      ...i.data(),
    }));

    pagesArray = pagesArray.map((p) => {
      if (
        p.versions &&
        p.versions.length > 0 &&
        p.versions.some(
          (v) =>
            typeof v.numberOfSuggestions !== 'number' ||
            typeof v.numberOfComments !== 'number',
        )
      ) {
        p.versions = p.versions.map((v) => {
          const foundVersion = pagesEditCounts.find((s) => s.id === v.id);

          return {
            ...v,
            numberOfSuggestions:
              (foundVersion.inspectChanges &&
                foundVersion.inspectChanges.length) ||
              0,
            numberOfComments:
              (foundVersion.comments && foundVersion.comments.length) || 0,
          };
        });

        updatePagesWithCounts.push(
          updateDoc(doc(db, 'projects', projectID, 'pages', p.id), {
            versions: p.versions,
          }),
        );
      }

      return p;
    });
  }

  const currentPage = yield select(selectors.currentPage);

  if (currentPage.id) {
    const updatedCurrentPage = pagesArray.find(
      ({ id }) => currentPage.id === id,
    );

    if (updatedCurrentPage) {
      yield put(pageSuccess(updatedCurrentPage));
    } else {
      const currentProject = yield select(selectors.currentProject);

      yield put(showSnackbar('Page does not exist.'));

      navigate(`/project/${currentProject.id}`);

      yield spawn(delayAndHideSnackbar);
    }
  }

  yield put(allPagesSuccess(pagesArray));
  yield put(hideLoading());

  if (updatePagesWithCounts.length > 0) yield all(updatePagesWithCounts);
}

function* cancelPagesChannel(channel) {
  yield take(actions.CANCEL_PAGES_LISTENER);
  channel.close();
}

function* allPagesSaga({ payload, navigate }) {
  const project = yield select(selectors.currentProject);
  const pagesColRef = query(
    collection(doc(db, 'projects', payload || project.id), 'pages'),
    where('isArchived', '==', false),
  );

  const pagesColChannel = yield call(createDocumentChangeChannel, pagesColRef);

  yield takeEvery(pagesColChannel, pagesChannel, {
    projectID: payload || project.id,
    navigate,
  });
  yield fork(cancelPagesChannel, pagesColChannel);
}

export function* watchAllPages() {
  yield takeLeading(actions.ALL_PAGES_REQUEST, allPagesSaga);
}

function tryGetPageHTML(url, count = 0) {
  const attempt = (u) => axios.get(u);

  return attempt(url).then(
    ({ data }) => data,
    () => {
      if (count > 10) return pageLoadingErrorMessage;
      count += 1;
      return new Promise((resolve) =>
        setTimeout(() => resolve(tryGetPageHTML(url, count)), 1000),
      );
    },
  );
}

function* pageVersionChannel(
  { projectID, pageID, versionID, navigate },
  versionDocRefSnap,
) {
  const versionDocRef = versionDocRefSnap.ref;
  const currentVersion = versionDocRefSnap.data();
  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentPage = yield select(selectors.currentPage);
  const storageVersionPath = `pages/${projectID}/${pageID}/${versionID}`;

  if (!currentVersion) {
    navigate(`/project/${projectID}`);
    yield put(
      showSnackbar('Error: Something went wrong while loading that page.'),
    );
    yield put(cancelPageVersionListener());
    yield put(toggleVersionLoading(false));
    return;
  }

  if (currentPage.url !== 'image') {
    if (currentVersion.inspectChanges)
      currentVersion.inspectChanges = currentVersion.inspectChanges.map((c) => {
        if (c.styleProperty === 'background-image')
          c.styleValue = `url('${
            c.styleValue.startsWith('gs://')
              ? convertGSToDownloadURL(c.styleValue)
              : c.styleValue
          }')`;

        if (c.changeType === 'image' && c.imageSrc.startsWith('gs'))
          c.imageSrc = convertGSToDownloadURL(c.imageSrc);

        return c;
      });

    if (!currentVersion.pageHTML) {
      let versionChanged = false;

      if (currentVersion.updatedCommentsHTML) {
        delete currentVersion.updatedCommentsHTML;
        versionChanged = true;
      }

      if (currentVersion.filesStorageLocation) {
        delete currentVersion.filesStorageLocation;
        versionChanged = true;
      }

      if (versionChanged) yield setDoc(versionDocRef, currentVersion);

      if (
        Object.keys(currentPageVersion).length === 0 ||
        (currentPageVersion.comments &&
          currentPageVersion.comments.length !==
            currentVersion.comments.length) ||
        (currentPageVersion.inspectChanges &&
          currentPageVersion.inspectChanges.length !==
            currentVersion.inspectChanges.length)
      ) {
        if (
          (currentPageVersion.comments &&
            currentPageVersion.comments.length !==
              currentVersion.comments.length) ||
          (currentPageVersion.inspectChanges &&
            currentPageVersion.inspectChanges.length !==
              currentVersion.inspectChanges.length)
        )
          yield delay(1000);

        let pageHTML = '';

        pageHTML = yield call(
          tryGetPageHTML,
          convertGSToDownloadURL(
            `pages/${projectID}/${pageID}/${versionID}/${
              currentVersion.pageHTMLInspected
                ? 'edited-HTML.html'
                : 'initial-HTML.html'
            }`,
          ),
        );

        if (
          !currentPageVersion.pageHTML ||
          currentPageVersion.pageHTML !== pageHTML
        )
          currentVersion.pageHTML = pageHTML;
      }
    } else {
      // Workaround for old way of storing pageHTML in firestore and moving to the new way.

      let finalHTML = '';

      if (
        currentVersion.pageHTML &&
        typeof currentVersion.pageHTML === 'string'
      ) {
        yield uploadString(
          ref(storage, `${storageVersionPath}/initial-HTML.html`),
          currentVersion.pageHTML,
        );
        finalHTML = currentVersion.pageHTML;
        delete currentVersion.pageHTML;
      }

      if (
        currentVersion.pageHTMLInspected &&
        typeof currentVersion.pageHTMLInspected === 'string'
      ) {
        yield uploadString(
          ref(storage, `${storageVersionPath}/edited-HTML.html`),
          currentVersion.pageHTMLInspected,
        );
        finalHTML = currentVersion.pageHTMLInspected;
        currentVersion.pageHTMLInspected = true;
      }

      if (currentVersion.pageHTMLCommented)
        delete currentVersion.pageHTMLCommented;

      yield setDoc(versionDocRef, currentVersion);
      if (!currentPageVersion.pageHTML) currentVersion.pageHTML = finalHTML;
    }
  } else {
    currentVersion.pageHTML = 'image';
  }

  currentVersion.id = versionDocRefSnap.id;

  yield put(
    currentVersionSuccess({ ...currentPageVersion, ...currentVersion }),
  );
  yield put(toggleVersionLoading(false));
}

function* cancelPageVersionChannel(channel) {
  yield take(actions.CANCEL_PAGE_VERSION_LISTENER);
  channel.close();
}

function* currentVersionRequestSaga({
  payload: { projectID, pageID, versionID },
  navigate,
}) {
  yield put(toggleVersionLoading(true));

  // DocumentReference: Current page version.
  const versionDocRef = doc(
    db,
    'projects',
    projectID,
    'pages',
    pageID,
    'versions',
    versionID,
  );
  const versionChannel = yield call(createDocumentChangeChannel, versionDocRef);

  yield takeEvery(versionChannel, pageVersionChannel, {
    projectID,
    pageID,
    versionID,
    navigate,
  });
  yield fork(cancelPageVersionChannel, versionChannel);
}

export function* watchCurrentVersionRequest() {
  yield takeLeading(actions.CURRENT_VERSION_REQUEST, currentVersionRequestSaga);
}

function* deletePage({ payload }) {
  const { id, type } = payload;
  const timestamp = Timestamp.now();
  const project = yield select(selectors.currentProject);
  const pagesColRef = collection(db, 'projects', project.id, 'pages');
  const pages = yield select(selectors.pages);
  const user = yield select(selectors.user);
  const batch = writeBatch(db);

  const filteredPages = pages
    .filter((i) => i.id !== id)
    .map((p) => ({
      createdAt: p.createdAt,
      id: p.id,
      numberOfVersions: p.versions.length || 1,
      url: p.url,
    }));

  try {
    batch.delete(doc(pagesColRef, id));
    batch.update(doc(db, 'projects', project.id), {
      modifiedAt: timestamp,
      pages: filteredPages,
    });
    yield batch.commit();
    yield put(showSnackbar(`${type === 'page' ? 'Page' : 'File'} deleted.`));
    logEvent(analytics, 'page_delete', {
      event_user: (user && user.uid) || null,
      project: (project && project.id) || null,
      project_page: id || null,
    });
    yield spawn(delayAndHideSnackbar);
  } catch (error) {
    /* Handle errors */
  }
}

export function* watchDeletePage() {
  const deletePageChannel = yield actionChannel(actions.DELETE_PAGE);

  while (true) {
    const payload = yield take(deletePageChannel);

    yield call(deletePage, payload);
  }
}

function* addCommentSaga({
  payload: {
    ids: { projectID, pageID, versionID },
    comment,
    commentID,
    userID,
    deviceSize,
    querySelector,
    dotPosition,
    activity = {},
    hasOnboarded,
    hasVideo = false,
    videoURL,
    videoDuration = null,
    files = [],
    floatingComment = false,
  },
  callback = () => {},
}) {
  const timestamp = Timestamp.now();
  const versionDocRef = doc(
    db,
    'projects',
    projectID,
    'pages',
    pageID,
    'versions',
    versionID,
  );
  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentPage = yield select(selectors.currentPage);
  const currentUser = yield select(selectors.user);
  let newComments = [];
  let foundCommentThread = {};
  const notificationType = ['comment'];
  const commentMentionRegExp = new RegExp('{@(.*?)}', 'g');
  const mentionedUsers = comment.match(commentMentionRegExp) || null;
  let commentReplyThreadInitiator = null;
  let commentThreadMembers = [];
  let newCurrentVersionChanges = {};
  let newCurrentPageChanges = {};

  if (mentionedUsers && mentionedUsers.length > 0)
    notificationType.push('mention');

  if (currentPageVersion.comments && currentPageVersion.comments.length > 0)
    newComments = [...currentPageVersion.comments];

  const promises = [];

  files.forEach(({ file }) => {
    promises.push(
      uploadBytes(
        ref(
          storage,
          `attachments/${projectID}/${pageID}/${versionID}/${file.name}`,
        ),
        file,
        {
          cacheControl: 'private,max-age=31536000',
        },
      ),
    );
  });

  if (
    newComments.some((c) => c.id === commentID && c.deviceSize === deviceSize)
  ) {
    foundCommentThread = { ...newComments.find((c) => c.id === commentID) };
    commentReplyThreadInitiator = foundCommentThread.thread[0].userID;
    foundCommentThread.thread.forEach((i) => {
      commentThreadMembers = [
        ...commentThreadMembers,
        ...(i.comment.match(commentMentionRegExp) || []),
      ];
    });

    // add here for comment thread
    foundCommentThread.thread.push({
      createdAt: timestamp,
      userID,
      comment,
      ...(hasVideo ? { hasVideo, videoURL, videoDuration } : {}),
      attachments: files.map(({ file }) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        path: `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/attachments/${projectID}/${pageID}/${versionID}/${file.name}`,
      })),
    });

    newComments = newComments.map((c) =>
      c.id === foundCommentThread.id ? foundCommentThread : c,
    );
    notificationType.push('reply');
  } else {
    // add here for new comment
    newComments.push({
      id: commentID,
      ...(floatingComment ? {} : { querySelector }),
      isResolved: false,
      thread: [
        {
          createdAt: timestamp,
          userID,
          comment,
          ...(hasVideo ? { hasVideo, videoURL, videoDuration } : {}),
          attachments: files.map(({ file }) => ({
            name: file.name,
            size: file.size,
            type: file.type,
            path: `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/attachments/${projectID}/${pageID}/${versionID}/${file.name}`,
          })),
        },
      ],
      deviceSize,
      ...(floatingComment ? {} : { dotPosition }),
      hasVideo,
      videoDuration,
      floatingComment,
    });
  }

  newCurrentVersionChanges = {
    comments: newComments,
    modifiedAt: timestamp,
  };
  newCurrentPageChanges = {
    modifiedAt: timestamp,
    versions: currentPage.versions.map((v) =>
      v.id === versionID ? { ...v, numberOfComments: newComments.length } : v,
    ),
  };

  yield allSettled(promises);

  if (callback) {
    callback();
  }

  // Update redux store: web.currentPageVersion.
  yield put(
    currentVersionSuccess({
      ...currentPageVersion,
      ...newCurrentVersionChanges,
    }),
  );
  yield put(
    pageSuccess({
      ...currentPage,
      ...newCurrentPageChanges,
    }),
  );

  yield updateDoc(versionDocRef, newCurrentVersionChanges);

  yield updateDoc(
    doc(db, 'projects', projectID, 'pages', pageID),
    newCurrentPageChanges,
  );
  yield updateDoc(doc(db, 'projects', projectID), { modifiedAt: timestamp });
  if (hasOnboarded === false) {
    const onboarding = { ...currentUser.onboarding, webFirstComment: true };

    yield put(signInSuccess({ onboarding }));
    yield updateDoc(doc(db, 'users', currentUser.uid), {
      onboarding,
    });
  }

  logEvent(analytics, 'page_comment_add', {
    event_user: userID || null,
    project: projectID || null,
    project_page: pageID || null,
    project_page_version: versionID || null,
    project_page_version_comment: commentID || null,
  });

  // Push notification.

  yield put(
    pushNotification({
      type: notificationType,
      context: {
        ...activity,
        commentID,
        projectID,
        pageID,
        versionID,
        url: currentPage.url,
        isPDF: currentPage.isPDF ?? false,
      },
      mentionedUsers,
      commentReplyThreadInitiator,
      commentThreadMembers,
    }),
  );
}

export function* watchAddComment() {
  yield takeLatest(actions.ADD_COMMENT, addCommentSaga);
}

function* resolveCommentSaga({
  payload: { isResolved, projectID, pageID, versionID, commentID },
}) {
  const timestamp = Timestamp.now();
  const currentProject = yield select(selectors.currentProject);
  const currentPage = yield select(selectors.currentPage);
  const currentPageVersion = yield select(selectors.currentPageVersion);
  const user = yield select(selectors.user);
  const versionDocRef = doc(
    db,
    'projects',
    projectID,
    'pages',
    pageID,
    'versions',
    versionID,
  );
  const batch = writeBatch(db);
  let comment = '';

  const newComments = currentPageVersion.comments.map((c) => {
    if (c.id === commentID) {
      comment = c.thread[0].comment;

      return { ...c, isResolved };
    }

    return c;
  });

  yield put(
    currentVersionSuccess({
      ...currentPageVersion,
      comments: newComments,
      modifiedAt: timestamp,
    }),
  );

  if (currentPageVersion.pageHTML) delete currentPageVersion.pageHTML;

  batch.update(versionDocRef, {
    comments: newComments,
    modifiedAt: timestamp,
  });
  batch.update(doc(db, 'projects', projectID, 'pages', pageID), {
    modifiedAt: timestamp,
  });
  batch.update(doc(db, 'projects', projectID), {
    modifiedAt: timestamp,
  });

  yield batch.commit();

  // Push notification.

  if (isResolved)
    yield put(
      pushNotification({
        type: 'web-comment-resolve',
        context: {
          commentID,
          projectID,
          pageID,
          versionID,
          pageTitle: currentPage.title,
          thumbnailURL: currentPage.thumbnail,
          comment,
        },
        collaborators: (currentProject.users ?? []).filter(
          (u) => u.uid !== user.uid,
        ),
      }),
    );

  // GA event.

  logEvent(analytics, 'page_comment_resolve', {
    event_user: (user && user.uid) || null,
    project: projectID || null,
    project_page: pageID || null,
    project_page_version: versionID || null,
    project_page_version_comment: commentID || null,
  });
}

export function* watchResolveComment() {
  const resolveCommentChannel = yield actionChannel(actions.RESOLVE_COMMENT);

  while (true) {
    const payload = yield take(resolveCommentChannel);

    yield call(resolveCommentSaga, payload);
  }
}

function* editCommentPositionSaga({ payload: { cid, dotPosition } }) {
  const user = yield select(selectors.user);
  const currentProject = yield select(selectors.currentProject);
  const currentPage = yield select(selectors.currentPage);
  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentVersionDocRef = doc(
    db,
    'projects',
    currentProject.id,
    'pages',
    currentPage.id,
    'versions',
    currentPageVersion.id,
  );
  const currentVersionDocSnap = yield getDoc(currentVersionDocRef);
  let newComments = currentVersionDocSnap.get('comments');

  newComments = newComments.map((c) => {
    if (c.id === cid) {
      const newDotPosition = { ...dotPosition };

      return { ...c, dotPosition: newDotPosition };
    }
    return c;
  });

  // Update redux store: web.currentPageVersion.
  yield put(
    currentVersionSuccess({
      ...currentPageVersion,
      comments: newComments,
    }),
  );
  yield updateDoc(currentVersionDocRef, {
    comments: newComments,
  });
  logEvent(analytics, 'page_comment_edit_position', {
    event_user: (user && user.uid) || null,
    project: (currentProject && currentProject.id) || null,
    project_page: (currentPage && currentPage.id) || null,
    project_page_version: (currentPageVersion && currentPageVersion.id) || null,
    project_page_version_comment: cid || null,
  });
}

export function* watchEditCommentPosition() {
  yield takeLatest(actions.EDIT_COMMENT_POSITION, editCommentPositionSaga);
}

function* editCommentSaga({
  payload: { cid, uid, createdAt, newComment, ids, files = [] },
}) {
  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentVersionDocRef = doc(
    db,
    'projects',
    ids.projectID,
    'pages',
    ids.pageID,
    'versions',
    ids.versionID,
  );
  const promises = [];

  files.forEach(({ file }) => {
    promises.push(
      uploadBytes(
        ref(
          storage,
          `attachments/${ids.projectID}/${ids.pageID}/${ids.versionID}/${file.name}`,
        ),
        file,
        {
          cacheControl: 'private,max-age=31536000',
        },
      ),
    );
  });

  let newComments = [...currentPageVersion.comments];

  const getNewAttachments = (p, n) => {
    const a1 = p.map((a) => JSON.stringify(a)).sort();
    const a2 = n.map((a) => JSON.stringify(a)).sort();

    if (isEqual(a1, a2)) {
      return a1;
    }

    return [...p, ...n];
  };

  /* eslint-disable */
  newComments = newComments.map((c) => {
    if (c.id === cid) {
      let newThread = c.thread;
      newThread = newThread.map((t) =>
        t.userID === uid && t.createdAt.valueOf() === createdAt.valueOf()
          ? {
              ...t,
              comment: newComment,
              attachments: getNewAttachments(
                t?.attachments ?? [],
                files.map(({ file }) => ({
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  path: `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/attachments/${ids.projectID}/${ids.pageID}/${ids.versionID}/${file.name}`,
                })),
              ),
            }
          : t,
      );
      return { ...c, thread: newThread };
    }
    return c;
  });
  /* eslint-enable */

  // Update redux store: web.currentPageVersion.
  yield put(
    currentVersionSuccess({
      ...currentPageVersion,
      comments: newComments,
    }),
  );

  yield updateDoc(currentVersionDocRef, {
    comments: newComments,
  });

  logEvent(analytics, 'page_comment_edit', {
    event_user: uid || null,
    project: ids.projectID || null,
    project_page: ids.pageID || null,
    project_page_version: ids.versionID || null,
    project_page_version_comment: cid || null,
  });
}

export function* watchEditComment() {
  yield takeEvery(actions.EDIT_COMMENT, editCommentSaga);
}

function* deleteCommentSaga({ payload: { cid, uid, createdAt, ids } }) {
  const timestamp = Timestamp.now();
  const currentPage = yield select(selectors.currentPage);
  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentProjectDocRef = doc(db, 'projects', ids.projectID);
  const currentPageDocRef = doc(currentProjectDocRef, 'pages', currentPage.id);
  const currentVersionDocRef = doc(
    currentPageDocRef,
    'versions',
    currentPageVersion.id,
  );
  let newComments = [...currentPageVersion.comments];
  let newCurrentVersionChanges = {};
  let newCurrentPageChanges = {};
  const batch = writeBatch(db);
  const promises = [];

  if (newComments.length === 0) return;

  newComments = newComments.map((c) => {
    if (c.id === cid) {
      let newThread = [...c.thread];

      if (
        newThread.length === 1 ||
        newThread
          .sort((a, b) => a.createdAt.valueOf() - b.createdAt.valueOf())[0]
          .createdAt.valueOf() === createdAt.valueOf()
      ) {
        if (c.thread[0].videoURL)
          promises.push(deleteObject(ref(storage, c.thread[0].videoURL)));

        return { ...c, thread: [] };
      }

      newThread = newThread.filter((t) => {
        const foundComment =
          t.userID === uid && t.createdAt.valueOf() === createdAt.valueOf();

        if (t.videoURL) promises.push(deleteObject(ref(storage, t.videoURL)));

        return !foundComment;
      });

      return { ...c, thread: newThread };
    }
    return c;
  });
  newComments = newComments.filter((c) => c.thread.length > 0);

  newCurrentVersionChanges = {
    comments: newComments,
    modifiedAt: timestamp,
  };
  newCurrentPageChanges = {
    modifiedAt: timestamp,
    versions: currentPage.versions.map((v) =>
      v.id === currentPageVersion.id
        ? { ...v, numberOfComments: newComments.length }
        : v,
    ),
  };

  // Update redux store: web.currentPageVersion.
  yield put(
    currentVersionSuccess({
      ...currentPageVersion,
      ...newCurrentVersionChanges,
    }),
  );
  yield put(pageSuccess({ ...currentPage, ...newCurrentPageChanges }));

  batch.update(currentVersionDocRef, newCurrentVersionChanges);
  batch.update(currentPageDocRef, newCurrentPageChanges);
  batch.update(currentProjectDocRef, { modifiedAt: timestamp });

  promises.push(batch.commit());

  yield allSettled(promises);

  logEvent(analytics, 'page_comment_delete', {
    event_user: uid || null,
    project: ids.projectID || null,
    project_page: ids.pageID || null,
    project_page_version: ids.versionID || null,
    project_page_version_comment: cid || null,
  });
}

export function* watchDeleteComment() {
  const deleteCommentChannel = yield actionChannel(actions.DELETE_COMMENT);

  while (true) {
    const payload = yield take(deleteCommentChannel);

    yield call(deleteCommentSaga, payload);
  }
}

function* updateVersionChangesSaga({
  payload: { projectID, pageID, versionID, changes, activity },
  callback = nullFunction,
}) {
  const currentUser = yield select(selectors.user);
  const currentPage = yield select(selectors.currentPage);
  const currentPageVersion = yield select(selectors.currentPageVersion);
  const oldChanges =
    (currentPageVersion.inspectChanges && [
      ...currentPageVersion.inspectChanges,
    ]) ||
    [];
  const timestamp = Timestamp.now();
  const projectDocRef = doc(db, 'projects', projectID);
  const pageDocRef = doc(projectDocRef, 'pages', pageID);
  const versionDocRef = doc(pageDocRef, 'versions', versionID);
  let newChanges = changes.map((i) => {
    const newChange = i;
    if (!i.userID) newChange.userID = currentUser.uid;
    if (!i.createdAt) newChange.createdAt = timestamp;
    newChange.groupCreatedAt = timestamp;
    return newChange;
  });
  const newVersion = {
    modifiedAt: timestamp,
    pageHTMLInspected: false,
  };
  const uploadImages = [];
  let newCurrentPageChanges = {};

  // Replaces border-radius of all sides with one if all are same.

  if (newChanges.some((i) => i.styleProperty === 'border-top-left-radius')) {
    const borderValues = newChanges.filter((i) =>
      i.styleProperty.match(/border-top-left-radius/g),
    );

    borderValues.forEach((i) => {
      if (
        newChanges.filter(
          (n) =>
            i.createdAt === n.createdAt &&
            i.querySelector === n.querySelector &&
            n.changeType === 'style' &&
            (n.styleProperty === 'border-top-left-radius' ||
              n.styleProperty === 'border-top-right-radius' ||
              n.styleProperty === 'border-bottom-left-radius' ||
              n.styleProperty === 'border-bottom-right-radius'),
        ).length === 4
      ) {
        newChanges = newChanges.filter(
          (n) =>
            !(
              i.createdAt === n.createdAt &&
              i.querySelector === n.querySelector &&
              n.changeType === 'style'
            ),
        );
        newChanges.push({ ...i, styleProperty: 'border-radius' });
      }
    });
  }

  // Replaces base64 URL with Cloud Storage URL for image changes.

  if (
    newChanges.some(
      (i) => i.styleProperty === 'background-image' || i.changeType === 'image',
    )
  )
    newChanges.forEach((c) => {
      if (
        c.styleProperty === 'background-image' &&
        c.styleValue.startsWith("url('data")
      ) {
        uploadImages.push({
          data: getURLFromCSSURL(c.styleValue),
          time: c.createdAt,
        });
        c.styleValue = `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/pages/${projectID}/${pageID}/${versionID}/${c.createdAt}`;
      }
      if (c.changeType === 'image' && c.imageSrc.startsWith('data')) {
        uploadImages.push({ data: c.imageSrc, time: c.createdAt });
        c.imageSrc = `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/pages/${projectID}/${pageID}/${versionID}/${c.createdAt}`;
      }
    });

  newVersion.inspectChanges = [
    ...oldChanges.map((i) => {
      if (
        i.styleProperty === 'background-image' &&
        i.styleValue.includes('https://firebasestorage.googleapis.com')
      )
        i.styleValue = convertDownloadURLToGS(i.styleValue);
      if (
        i.changeType === 'image' &&
        i.imageSrc.includes('https://firebasestorage.googleapis.com')
      )
        i.imageSrc = convertDownloadURLToGS(i.imageSrc);
      return i;
    }),
    ...newChanges,
  ];

  if (uploadImages.length > 0)
    yield all(
      uploadImages.map((i) =>
        uploadString(
          ref(storage, `pages/${projectID}/${pageID}/${versionID}/${i.time}`),
          i.data,
          'data_url',
        ),
      ),
    );

  newCurrentPageChanges = {
    modifiedAt: timestamp,
    versions: currentPage.versions.map((v) =>
      v.id === currentPageVersion.id
        ? { ...v, numberOfSuggestions: newVersion.inspectChanges.length }
        : v,
    ),
  };

  yield put(currentVersionSuccess({ ...currentPageVersion, ...newVersion }));
  yield put(pageSuccess({ ...currentPage, ...newCurrentPageChanges }));
  yield put(showSnackbar('New changes have been saved successfully.'));
  yield updateDoc(versionDocRef, newVersion);
  yield updateDoc(pageDocRef, newCurrentPageChanges);
  yield updateDoc(projectDocRef, { modifiedAt: timestamp });

  logEvent(analytics, 'page_inspect_add', {
    event_user: (currentUser && currentUser.uid) || null,
    project: projectID || null,
    project_page: pageID || null,
    project_page_version: versionID || null,
  });

  callback();

  // Push notifications.

  if (activity && newChanges.length)
    yield put(
      pushNotification({
        type: ['inspect'],
        context: {
          projectID,
          pageID,
          versionID,
        },
        inspectChangesLength: newChanges.length,
        activity,
      }),
    );

  yield spawn(delayAndHideSnackbar);
}

export function* watchUpdateVersionChanges() {
  yield takeLatest(actions.UPDATE_VERSION_CHANGES, updateVersionChangesSaga);
}

function* deleteInspectChangeSaga({
  payload: { projectID, pageID, versionID, changes },
}) {
  const currentPage = yield select(selectors.currentPage);
  const currentPageVersion = yield select(selectors.currentPageVersion);
  const pageDocRef = doc(db, 'projects', projectID, 'pages', pageID);

  if (!projectID || !pageID || !versionID || !changes) return;

  const versions = currentPage.versions.map((v) =>
    v.id === versionID ? { ...v, numberOfSuggestions: changes.length } : v,
  );

  yield put(
    currentVersionSuccess({ ...currentPageVersion, inspectChanges: changes }),
  );
  yield put(pageSuccess({ ...currentPage, versions }));
  yield updateDoc(pageDocRef, { versions });
  yield updateDoc(doc(pageDocRef, 'versions', versionID), {
    inspectChanges: changes,
  });

  yield put(
    showSnackbar(
      `Suggestion${changes.length !== 1 ? 's' : ''} deleted successfully.`,
    ),
  );
  yield spawn(delayAndHideSnackbar);
}

export function* watchDeleteInspectChange() {
  const deleteInspectChangeChannel = yield actionChannel(
    actions.DELETE_INSPECT_CHANGE,
  );

  while (true) {
    const payload = yield take(deleteInspectChangeChannel);

    yield call(deleteInspectChangeSaga, payload);
  }
}

function* updatePageTitleSaga({ payload: { id, title, pageID } }) {
  const pages = yield select(selectors.pages);
  const user = yield select(selectors.user);

  try {
    let updatedPages = [];

    yield updateDoc(doc(db, 'projects', id, 'pages', pageID), {
      title,
    });
    updatedPages = pages.map((p) => (p.id === pageID ? { ...p, title } : p));
    yield put(allPagesSuccess(updatedPages));
    logEvent(analytics, 'page_title_update', {
      event_user: (user && user.uid) || null,
      project: id || null,
      project_page: pageID || null,
    });
  } catch (error) {
    // Handle errors
  }
}

export function* watchUpdatePageTitle() {
  yield takeLatest(actions.UPDATE_PAGE_TITLE, updatePageTitleSaga);
}

function* togglePageApprovalWorkerSaga() {
  const page = yield select(selectors.currentPage);
  const project = yield select(selectors.currentProject);
  const pages = yield select(selectors.pages);
  const user = yield select(selectors.user);
  const version = yield select(selectors.currentPageVersion);

  const alreadyApproved = !!page.isApproved;

  /* eslint-disable indent */
  const isApprovedObj = alreadyApproved
    ? null
    : {
        uid: user.uid,
        date: Date.now(),
      };
  /* eslint-enable indent */

  yield updateDoc(doc(db, 'projects', project.id, 'pages', page.id), {
    isApproved: isApprovedObj,
  });

  /* eslint-disable indent */
  const newPages = pages.map((c) =>
    c.id === page.id
      ? {
          ...c,
          isApproved: isApprovedObj,
        }
      : c,
  );
  /* eslint-enable indent */

  yield put(
    pageSuccess({
      ...page,
      isApproved: isApprovedObj,
    }),
  );

  yield put(allPagesSuccess(newPages));

  yield put(
    showSnackbar(
      !alreadyApproved
        ? 'This page is marked as approved'
        : 'This page is marked as not approved',
    ),
  );

  yield spawn(delayAndHideSnackbar);

  if (!alreadyApproved) {
    const to = project.users
      .filter(
        ({ emailPreferences: { pagesApproved } = defaultEmailPreferences }) =>
          pagesApproved,
      )
      .map(({ email }) => email)
      .join(',');

    if (to !== '') {
      yield axios.post(
        `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendEmail`,
        {
          from: `Team ruttl <support@ruttl.com>`,
          to,
          subject: `The webpage ${page.title} on Ruttl project ${project.title} has been marked as ‘Approved’`,
          template: 'page-approved',
          templateVariables: JSON.stringify({
            project: project.title,
            url: `${window.location.origin}/project/${project.id}/${page.id}`,
            title: page.title,
            approver: user.displayName,
          }),
        },
      );

      yield put(
        pushNotification({
          type: 'page-approved',
          context: {
            page: page.title,
            pageID: page.id,
            project: project.title,
            projectID: project.id,
            versionID: version.id,
          },
          collaborators: project.users.filter(({ uid }) => uid !== user.uid),
        }),
      );
    }
  } else {
    const to = project.users
      .filter(
        ({ emailPreferences: { pagesUnapproved } = defaultEmailPreferences }) =>
          pagesUnapproved,
      )
      .map(({ email }) => email)
      .join(',');

    if (to !== '') {
      yield axios.post(
        `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendEmail`,
        {
          from: `Team ruttl <support@ruttl.com>`,
          to,
          subject: `The webpage ${page.title} on Ruttl project ${project.title} has been marked as ‘Unapproved’`,
          template: 'page-unapproved',
          templateVariables: JSON.stringify({
            project: project.title,
            url: `${window.location.origin}/project/${project.id}`,
            page: page.title,
            approver: user.displayName,
          }),
        },
      );

      yield put(
        pushNotification({
          type: 'page-unapproved',
          context: {
            page: page.title,
            pageID: page.id,
            project: project.title,
            projectID: project.id,
            versionID: version.id,
          },
          collaborators: project.users.filter(({ uid }) => uid !== user.uid),
        }),
      );
    }
  }
}

export function* togglePageApprovalWatcherSaga() {
  yield takeLatest(actions.TOGGLE_PAGE_APPROVAL, togglePageApprovalWorkerSaga);
}

function* deletePageVersionSaga({
  payload: { projectID, pageID, versionID, shouldReload = false },
  navigate,
}) {
  if (!projectID || !pageID || !versionID) return;

  const batch = writeBatch(db);
  const page = yield select(selectors.currentPage);
  const project = yield select(selectors.currentProject);
  const projectRef = doc(db, 'projects', projectID);
  const pageRef = doc(projectRef, 'pages', pageID);
  const versionRef = doc(pageRef, 'versions', versionID);

  yield put(cancelPageVersionListener());

  batch.update(projectRef, {
    pages: project.pages.map((p) =>
      p.id === pageID ? { ...p, numberOfVersions: p.numberOfVersions - 1 } : p,
    ),
  });
  batch.update(pageRef, {
    versions: page.versions.filter((v) => v.id !== versionID),
  });
  batch.delete(versionRef);

  yield batch.commit();

  if (shouldReload) navigate(`/project/${projectID}/${pageID}`);
}

export function* watchDeletePageVersion() {
  const deletePageVersionChannel = yield actionChannel(
    actions.DELETE_PAGE_VERSION,
  );

  while (true) {
    const payload = yield take(deletePageVersionChannel);

    yield call(deletePageVersionSaga, payload);
  }
}

function* renamePageVersionSaga({
  payload: { projectID, pageID, versionID, title },
}) {
  const page = yield select(selectors.currentPage);
  const projectRef = doc(db, 'projects', projectID);
  const pageRef = doc(projectRef, 'pages', pageID);
  const versionRef = doc(pageRef, 'versions', versionID);
  const batch = writeBatch(db);

  batch.update(pageRef, {
    versions: page.versions.map((v) =>
      v.id === versionID ? { ...v, title } : v,
    ),
  });
  batch.update(versionRef, { title });
  yield batch.commit();
}

export function* watchRenamePageVersion() {
  yield takeLatest(actions.RENAME_PAGE_VERSION, renamePageVersionSaga);
}

function* toggleChangeResolveWorkerSaga({
  payload: { projectID, pageID, versionID, changes },
}) {
  const currentVersion = yield select(selectors.currentPageVersion);
  const projectDocRef = doc(db, 'projects', projectID);
  const pageDocRef = doc(projectDocRef, 'pages', pageID);
  const versionDocRef = doc(pageDocRef, 'versions', versionID);
  const user = yield select(selectors.user);

  const { inspectChanges = [] } = currentVersion;

  /* eslint-disable indent */
  const newInspectChanges = inspectChanges.map(({ resolvedBy, ...c }) =>
    changes.some(
      (i) =>
        i.id === c.id &&
        i.changeType === c.changeType &&
        i.createdAt === c.createdAt &&
        (i.styleProperty ? i.styleProperty === c.styleProperty : true),
    )
      ? {
          ...c,
          ...(resolvedBy ? {} : { resolvedBy: user.uid }),
        }
      : { ...c, ...(resolvedBy ? { resolvedBy } : {}) },
  );
  /* eslint-enable indent */

  yield updateDoc(versionDocRef, {
    inspectChanges: newInspectChanges,
  });

  yield put(
    currentVersionSuccess({
      ...currentVersion,
      inspectChanges: newInspectChanges,
    }),
  );
}

export function* toggleChangeResolveWatcherSaga() {
  yield takeLatest(
    actions.TOGGLE_CHANGE_RESOLVE,
    toggleChangeResolveWorkerSaga,
  );
}

function* updateAsanaCommentWorkerSaga({ payload, callback }) {
  const {
    projectID,
    pageID,
    versionID,
    commentID,
    threadIndex,
    asanaCardURL,
    asanaCardID,
  } = payload;

  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentUser = yield select(selectors.user);

  /* eslint-disable */
  const newComments = currentPageVersion.comments.map((c) =>
    c.id === commentID
      ? {
          ...c,
          thread: c.thread.map((t, i) =>
            i === threadIndex
              ? {
                  ...t,
                  asanaCardURL,
                  asanaCardID,
                  asanaCardCreatedBy: currentUser.uid,
                }
              : t,
          ),
        }
      : c,
  );
  /* eslint-enable */

  yield updateDoc(
    doc(db, 'projects', projectID, 'pages', pageID, 'versions', versionID),
    { comments: newComments },
  );

  yield put(showSnackbar('Comment added to Asana.'));

  callback();

  yield spawn(delayAndHideSnackbar);
}

export function* updateAsanaCommentWatcherSaga() {
  yield takeLatest(actions.UPDATE_ASANA_COMMENT, updateAsanaCommentWorkerSaga);
}

function* updateAsanaEditWorkerSaga({ payload, callback }) {
  const { projectID, pageID, versionID, asanaCardURL, asanaCardID, changes } =
    payload;

  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentUser = yield select(selectors.user);

  const { inspectChanges = [] } = currentPageVersion;

  const getDate = (i) => {
    if (i.groupCreatedAt) return i.groupCreatedAt.toDate();

    return dayjs(
      typeof i.createdAt === 'number' ? i.createdAt : i.createdAt.toDate(),
    ).startOf('day');
  };

  /* eslint-disable indent */
  const newInspectChanges = inspectChanges
    .map((c) => ({ ...c, d: getDate(c) }))
    .map((c) =>
      changes.some((i) => dayjs(c.d).isSame(i.d))
        ? {
            ...c,
            asanaCardURL,
            asanaCardID,
            asanaCardCreatedBy: currentUser.uid,
          }
        : c,
    );
  /* eslint-enable indent */

  yield updateDoc(
    doc(db, 'projects', projectID, 'pages', pageID, 'versions', versionID),
    { inspectChanges: newInspectChanges },
  );

  yield put(showSnackbar('Edit added to Asana.'));

  callback();

  yield spawn(delayAndHideSnackbar);
}

export function* updateAsanaEditWatcherSaga() {
  yield takeLatest(actions.UPDATE_ASANA_EDIT, updateAsanaEditWorkerSaga);
}

function* updateTrelloCommentWorkerSaga({ payload, callback }) {
  const {
    projectID,
    pageID,
    versionID,
    commentID,
    threadIndex,
    trelloCardURL,
    trelloCardID,
  } = payload;

  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentUser = yield select(selectors.user);

  /* eslint-disable */
  const newComments = currentPageVersion.comments.map((c) =>
    c.id === commentID
      ? {
          ...c,
          thread: c.thread.map((t, i) =>
            i === threadIndex
              ? {
                  ...t,
                  trelloCardURL,
                  trelloCardID,
                  trelloCardCreatedBy: currentUser.uid,
                }
              : t,
          ),
        }
      : c,
  );
  /* eslint-enable */

  yield updateDoc(
    doc(db, 'projects', projectID, 'pages', pageID, 'versions', versionID),
    { comments: newComments },
  );

  yield put(showSnackbar('Comment added to Trello.'));

  callback();

  yield spawn(delayAndHideSnackbar);
}

export function* updateTrelloCommentWatcherSaga() {
  yield takeLatest(
    actions.UPDATE_TRELLO_COMMENT,
    updateTrelloCommentWorkerSaga,
  );
}

function* updateTrelloEditWorkerSaga({ payload, callback }) {
  const { projectID, pageID, versionID, trelloCardURL, trelloCardID, changes } =
    payload;

  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentUser = yield select(selectors.user);

  const { inspectChanges = [] } = currentPageVersion;

  const getDate = (i) => {
    if (i.groupCreatedAt) return i.groupCreatedAt.toDate();

    return dayjs(
      typeof i.createdAt === 'number' ? i.createdAt : i.createdAt.toDate(),
    ).startOf('day');
  };

  /* eslint-disable indent */
  const newInspectChanges = inspectChanges
    .map((c) => ({ ...c, d: getDate(c) }))
    .map((c) =>
      changes.some((i) => dayjs(c.d).isSame(i.d))
        ? {
            ...c,
            trelloCardURL,
            trelloCardID,
            trelloCardCreatedBy: currentUser.uid,
          }
        : c,
    );
  /* eslint-enable indent */

  yield updateDoc(
    doc(db, 'projects', projectID, 'pages', pageID, 'versions', versionID),
    { inspectChanges: newInspectChanges },
  );

  yield put(showSnackbar('Edit added to Trello.'));

  callback();

  yield spawn(delayAndHideSnackbar);
}

export function* updateTrelloEditWatcherSaga() {
  yield takeLatest(actions.UPDATE_TRELLO_EDIT, updateTrelloEditWorkerSaga);
}

function* deleteAsanaCommentWorkerSaga({ payload, callback }) {
  const { projectID, pageID, versionID, commentID, threadIndex } = payload;

  const currentPageVersion = yield select(selectors.currentPageVersion);

  /* eslint-disable */
  const newComments = currentPageVersion.comments.map((c) =>
    c.id === commentID
      ? {
          ...c,
          thread: c.thread.map(
            ({ asanaCardURL, asanaCardID, asanaCardCreatedBy, ...t }, i) =>
              i === threadIndex ? t : t,
          ),
        }
      : c,
  );
  /* eslint-enable */

  yield updateDoc(
    doc(db, 'projects', projectID, 'pages', pageID, 'versions', versionID),
    { comments: newComments },
  );

  yield put(showSnackbar('Comment deleted on Asana.'));

  callback();

  yield spawn(delayAndHideSnackbar);
}

export function* deleteAsanaCommentWatcherSaga() {
  yield takeLatest(actions.DELETE_ASANA_COMMENT, deleteAsanaCommentWorkerSaga);
}

function* deleteAsanaEditWorkerSaga({ payload, callback }) {
  const { projectID, pageID, versionID, changes } = payload;

  const currentPageVersion = yield select(selectors.currentPageVersion);

  const { inspectChanges = [] } = currentPageVersion;

  const getDate = (i) => {
    if (i.groupCreatedAt) return i.groupCreatedAt.toDate();

    return dayjs(
      typeof i.createdAt === 'number' ? i.createdAt : i.createdAt.toDate(),
    ).startOf('day');
  };

  const newInspectChanges = inspectChanges
    .map((c) => ({ ...c, d: getDate(c) }))
    .map(({ asanaCardURL, asanaCardID, asanaCardCreatedBy, ...c }) =>
      changes.some((i) => dayjs(c.d).isSame(i.d)) ? c : c,
    );

  yield updateDoc(
    doc(db, 'projects', projectID, 'pages', pageID, 'versions', versionID),
    { inspectChanges: newInspectChanges },
  );

  yield put(showSnackbar('Edit deleted on Asana.'));

  callback();

  yield spawn(delayAndHideSnackbar);
}

export function* deleteAsanaEditWatcherSaga() {
  yield takeLatest(actions.DELETE_ASANA_EDIT, deleteAsanaEditWorkerSaga);
}

function* deleteTrelloCommentWorkerSaga({ payload, callback }) {
  const { projectID, pageID, versionID, commentID, threadIndex } = payload;

  const currentPageVersion = yield select(selectors.currentPageVersion);

  /* eslint-disable */
  const newComments = currentPageVersion.comments.map((c) =>
    c.id === commentID
      ? {
          ...c,
          thread: c.thread.map(
            ({ trelloCardURL, trelloCardID, trelloCardCreatedBy, ...t }, i) =>
              i === threadIndex ? t : t,
          ),
        }
      : c,
  );
  /* eslint-enable */

  yield updateDoc(
    doc(db, 'projects', projectID, 'pages', pageID, 'versions', versionID),
    { comments: newComments },
  );

  yield put(showSnackbar('Comment deleted on Trello.'));

  callback();

  yield spawn(delayAndHideSnackbar);
}

export function* deleteTrelloCommentWatcherSaga() {
  yield takeLatest(
    actions.DELETE_TRELLO_COMMENT,
    deleteTrelloCommentWorkerSaga,
  );
}

function* deleteTrelloEditWorkerSaga({ payload, callback }) {
  const { projectID, pageID, versionID, changes } = payload;

  const currentPageVersion = yield select(selectors.currentPageVersion);

  const { inspectChanges = [] } = currentPageVersion;

  const getDate = (i) => {
    if (i.groupCreatedAt) return i.groupCreatedAt.toDate();

    return dayjs(
      typeof i.createdAt === 'number' ? i.createdAt : i.createdAt.toDate(),
    ).startOf('day');
  };

  const newInspectChanges = inspectChanges
    .map((c) => ({ ...c, d: getDate(c) }))
    .map(({ trelloCardURL, trelloCardID, trelloCardCreatedBy, ...c }) =>
      changes.some((i) => dayjs(c.d).isSame(i.d)) ? c : c,
    );

  yield updateDoc(
    doc(db, 'projects', projectID, 'pages', pageID, 'versions', versionID),
    { inspectChanges: newInspectChanges },
  );

  yield put(showSnackbar('Edit deleted on Trello.'));

  callback();

  yield spawn(delayAndHideSnackbar);
}

export function* deleteTrelloEditWatcherSaga() {
  yield takeLatest(actions.DELETE_TRELLO_EDIT, deleteTrelloEditWorkerSaga);
}

const resizeFile = (file) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      500,
      500,
      'JPEG',
      50,
      0,
      (uri) => {
        resolve(uri);
      },
      'base64',
      500,
      500,
    );
  });

const loadImage = (thumb) =>
  new Promise((resolve, reject) => {
    const img = new Image(500, 350);
    img.src = thumb;
    img.style.position = 'fixed';
    img.style.pointerEvents = 'none';
    img.style.opacity = '0';
    img.style.objectFit = 'cover';
    img.style.objectPosition = 'top';
    document.body.appendChild(img);
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

function* generateThumbnail(file) {
  const thumb = yield resizeFile(file);
  const imgEl = yield loadImage(thumb);
  const canvas = document.createElement('canvas');

  canvas.width = 500;
  canvas.height = 350;

  const ctx = canvas.getContext('2d');

  ctx.drawImage(imgEl, 0, 0, 500, 350, 0, 0, 500, 350);

  const imgDataURL = canvas.toDataURL('image/jpeg');

  imgEl.remove();
  canvas.remove();

  return imgDataURL;
}

function* addImageSaga({ payload, callback, navigate }) {
  const currentProject = yield select(selectors.currentProject);
  const currentPage = yield select(selectors.currentPage);
  const timestamp = Timestamp.now();
  const user = yield select(selectors.user);
  const pages = yield select(selectors.pages);
  const projectRef = doc(db, 'projects', currentProject.id);
  let pageRef = null;
  let versionRef = null;
  const newPage = {
    id: 'new',
    createdAt: timestamp,
    modifiedAt: timestamp,
    url: 'image',
    isArchived: false,
    title:
      payload.name ||
      getFileNameWithoutExt(payload.file) ||
      (payload.isPDF ? 'PDF' : 'image'),
    versions: [],
    isPDF: payload.isPDF || false,
  };
  const newVersion = {
    createdAt: timestamp,
    createdBy: user.uid,
  };

  // yield put(allPagesSuccess([newPage, ...pages]));

  if (Object.keys(currentPage).length > 0) {
    pageRef = doc(projectRef, 'pages', currentPage.id);
  } else {
    const { id, ...newPageData } = newPage;

    pageRef = yield addDoc(collection(projectRef, 'pages'), newPageData);
  }

  yield put(pushPagesCreating(pageRef.id));

  versionRef = yield addDoc(collection(pageRef, 'versions'), newVersion);
  yield updateDoc(pageRef, {
    modifiedAt: timestamp,
    versions: arrayUnion({
      ...newVersion,
      numberOfComments: 0,
      id: versionRef.id,
    }),
  });
  yield updateDoc(projectRef, {
    modifiedAt: timestamp,
    /* eslint-disable indent */
    pages: [
      ...pages
        .map((p) => ({
          id: p.id,
          url: p.url,
          createdAt: p.createdAt,
          numberOfVersions: p.versions.length,
        }))
        .map((p) =>
          p.id === pageRef.id && Object.keys(currentPage).length > 0
            ? {
                ...p,
                numberOfVersions: p.numberOfVersions + 1,
              }
            : p,
        ),
    ].concat(
      Object.keys(currentPage).length > 0
        ? []
        : [
            {
              id: pageRef.id,
              createdAt: newPage.createdAt,
              url: newPage.url,
              numberOfVersions: 1,
            },
          ],
    ),
    /* eslint-enable indent */
  });

  const thumbnail = yield generateThumbnail(payload.file);

  yield uploadString(
    ref(storage, `thumbnails/${currentProject.id}-${pageRef.id}.jpg`),
    thumbnail,
    'data_url',
    {
      cacheControl: 'private,max-age=31536000',
    },
  );
  yield uploadBytes(
    ref(
      storage,
      `pages/${currentProject.id}/${pageRef.id}/${versionRef.id}/image`,
    ),
    payload.file,
    {
      cacheControl: 'private,max-age=31536000',
    },
  );

  yield put(popPagesCreating(pageRef.id));

  yield put(
    showSnackbar(
      `New ${payload.isPDF ? 'PDF' : 'image'} added to your project.`,
    ),
  );

  if (Object.keys(currentPage).length > 0)
    navigate(
      `/project/${currentProject.id}/${pageRef.id}/${versionRef.id}/comment`,
    );

  if (callback) callback();

  yield spawn(delayAndHideSnackbar);
}

export function* watchAddImage() {
  yield takeEvery(actions.ADD_IMAGE, addImageSaga);
}

function* findSimilarPagesSaga({
  payload: { url, data, isScreenshot = false },
}) {
  let pages = [];

  try {
    const links = findPageLinks(data, url);

    if (links) pages = [...links.map((p) => ({ ...p, isScreenshot }))];
  } catch (err) {
    //
  }

  yield put(
    toggleSimilarPagesModal({
      show: pages.length > 0,
      pages,
      url,
    }),
  );
}

export function* watchFindSimilarPagesSaga() {
  yield takeLatest(actions.FIND_SIMILAR_PAGES, findSimilarPagesSaga);
}

function* sortPagesWorkerSaga({ payload }) {
  const batch = writeBatch(db);
  const project = yield select(selectors.currentProject);
  const projectRef = doc(db, 'projects', project.id);

  yield put(allPagesSuccess(payload));

  payload.forEach((p) => {
    batch.update(doc(projectRef, 'pages', p.id), {
      sortOrder: p.sortOrder,
    });
  });

  yield batch.commit();
}

export function* sortPagesWatcherSaga() {
  yield takeEvery(actions.SORT_PAGES, sortPagesWorkerSaga);
}

function* deleteVideoWorkerSaga({
  payload: {
    projectID,
    pageID,
    versionID,
    commentID,
    threadIndex,
    uid,
    videoURL,
  },
}) {
  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentVersionDocRef = doc(
    db,
    'projects',
    projectID,
    'pages',
    pageID,
    'versions',
    versionID,
  );
  let newComments = [...currentPageVersion.comments];

  newComments = newComments.map((c) => {
    if (c.id === commentID) {
      let newThread = c.thread;
      newThread = newThread.map((t, index) =>
        index === threadIndex
          ? { ...t, hasVideo: false, videoURL: null, videoDuration: null }
          : t,
      );
      return {
        ...c,
        thread: newThread,
        ...(c.videoDuration ? { videoDuration: null } : {}),
      };
    }
    return c;
  });

  // Update redux store: web.currentPageVersion.
  yield put(
    currentVersionSuccess({
      ...currentPageVersion,
      comments: newComments,
    }),
  );

  yield updateDoc(currentVersionDocRef, {
    comments: newComments,
  });

  yield deleteObject(ref(storage, videoURL));

  yield put(showSnackbar('Video deleted.'));

  yield spawn(delayAndHideSnackbar);

  logEvent(analytics, 'page_comment_edit', {
    event_user: uid || null,
    project: projectID || null,
    project_page: pageID || null,
    project_page_version: versionID || null,
    project_page_version_comment: commentID || null,
  });
}

export function* deleteVideoWatcherSaga() {
  yield takeLatest(actions.DELETE_VIDEO, deleteVideoWorkerSaga);
}

function* deleteAttachmentWorkerSaga({
  payload: {
    projectID,
    pageID,
    versionID,
    commentID,
    threadIndex,
    uid,
    attachmentURL,
    attachmentID,
    callback,
  },
}) {
  const currentPageVersion = yield select(selectors.currentPageVersion);
  const currentVersionDocRef = doc(
    db,
    'projects',
    projectID,
    'pages',
    pageID,
    'versions',
    versionID,
  );
  let newComments = [...currentPageVersion.comments];

  newComments = newComments.map((c) => {
    if (c.id === commentID) {
      let newThread = c.thread;

      /* eslint-disable */
      newThread = newThread.map((t, index) =>
        index === threadIndex
          ? {
              ...t,
              ...(t.attachments
                ? {
                    attachments: t.attachments.filter(
                      (_, i) => i !== attachmentID,
                    ),
                  }
                : {}),
            }
          : t,
      );
      /* eslint-enable */

      return {
        ...c,
        thread: newThread,
      };
    }
    return c;
  });

  // Update redux store: web.currentPageVersion.
  yield put(
    currentVersionSuccess({
      ...currentPageVersion,
      comments: newComments,
    }),
  );

  if (callback) {
    callback();
  }

  yield updateDoc(currentVersionDocRef, {
    comments: newComments,
  });

  yield deleteObject(ref(storage, attachmentURL));

  yield put(showSnackbar('Attachment deleted.'));

  yield spawn(delayAndHideSnackbar);

  logEvent(analytics, 'page_comment_edit', {
    event_user: uid || null,
    project: projectID || null,
    project_page: pageID || null,
    project_page_version: versionID || null,
    project_page_version_comment: commentID || null,
  });
}

export function* deleteAttachmentWatcherSaga() {
  yield takeLatest(actions.REMOVE_ATTACHMENT, deleteAttachmentWorkerSaga);
}

function* updateAllPageVersionsSaga() {
  let pages = yield select(selectors.pages);

  pages = pages.filter((p) => {
    if (p.isApproved || p.isArchived || p.isDemo) return false;

    if (p.url === 'image') return !!p.screenshotSource;

    return true;
  });

  if (pages.length > 0) {
    yield put(showSnackbar('Updating all pages/screenshots...'));

    let settledPromises = yield allSettled(
      pages.map((p) => {
        const addURL = new URL(p.url === 'image' ? p.screenshotSource : p.url)
          .href;

        return axios
          .get(`/fetcher/${addURL}`, {
            headers: { accept: 'text/html' },
          })
          .then(({ data }) => ({ data, ...p }))
          .catch(() => ({}));
      }),
    );

    settledPromises = settledPromises.filter(
      (p) => p.status === 'fulfilled' && !!p.value.data,
    );

    if (settledPromises.length > 0)
      yield all(
        settledPromises.map((p, index) =>
          put(
            addWebPage({
              data: p.value.data,
              url: p.value.screenshotSource || p.value.url,
              isScreenshot:
                p.value.url === 'image' &&
                typeof p.value.screenshotSource === 'string',
              paginate: { index: index + 1, total: settledPromises.length },
            }),
          ),
        ),
      );
    else yield put(showSnackbar('No pages were updated.'));
  } else {
    yield put(showSnackbar('No pages to update.'));
  }
}

export function* watchUpdateAllPageVersions() {
  yield takeLatest(actions.UPDATE_ALL_PAGE_VERSIONS, updateAllPageVersionsSaga);
}

function* copyUnresolvedCommentsSaga() {
  const project = yield select(selectors.currentProject);
  const currentPage = yield select(selectors.currentPage);
  const currentVersion = yield select(selectors.currentPageVersion);
  const currentPageVersions = currentPage.versions.sort(
    (a, b) => a.createdAt.valueOf() - b.createdAt.valueOf(),
  );

  const pullFromVersion =
    currentPageVersions[
      currentPageVersions.findIndex((v) => v.id === currentVersion.id) - 1
    ]?.id || null;

  if (pullFromVersion) {
    yield put(showSnackbar('Pulling comments...'));

    const versionDocSnap = yield getDoc(
      doc(
        db,
        'projects',
        project.id,
        'pages',
        currentPage.id,
        'versions',
        pullFromVersion,
      ),
    );

    const previousVersionComments = (
      versionDocSnap.get('comments') || []
    ).filter((c) => !c.isResolved);

    if (previousVersionComments.length) {
      const uniqueComments = previousVersionComments.filter(
        (c) =>
          !(
            Array.isArray(currentVersion.comments)
              ? currentVersion.comments
              : []
          ).some((i) => i.id === c.id),
      );

      if (uniqueComments.length)
        yield updateDoc(
          doc(
            db,
            'projects',
            project.id,
            'pages',
            currentPage.id,
            'versions',
            currentVersion.id,
          ),
          {
            comments: [
              ...(Array.isArray(currentVersion.comments)
                ? currentVersion.comments
                : []),
              ...uniqueComments.map((c) => ({
                ...c,
                copiedFromVersion: pullFromVersion,
              })),
            ],
          },
        );

      yield put(
        showSnackbar(
          `${uniqueComments.length} unresolved comment${
            uniqueComments.length === 1 ? '' : 's'
          } copied.`,
        ),
      );
    } else {
      yield put(
        showSnackbar('No unresolved comments found on previous version.'),
      );
    }

    yield delay(5000);
    yield put(hideSnackbar());
  } else {
    yield put(showSnackbar('Error occured while pulling comments.'));
  }
}

export function* watchCopyUnresolvedComments() {
  yield takeLatest(
    actions.COPY_UNRESOLVED_COMMENTS,
    copyUnresolvedCommentsSaga,
  );
}
